/**
 * useSettingsData — Data hook for the Settings page.
 *
 * Handles knowledge (storages) state only. Audience data has been extracted
 * to `app/audiences/hooks/useAudiencesData.ts` as part of the audiences
 * page migration.
 */

import { useState, useEffect, useCallback } from 'react'
import { storagesApi } from '@/lib/api'
import { toast as notify } from 'sonner'
import i18n, { intlLocaleForUi } from '@/i18n'

export function useSettingsData(activeTab: string) {
  // Knowledge (Storages) state
  const [knowledge, setKnowledge] = useState<any[]>([])
  const [knowledgeLoading, setKnowledgeLoading] = useState(false)
  const [knowledgeLoaded, setKnowledgeLoaded] = useState(false)
  const [deletingKnowledgeId, setDeletingKnowledgeId] = useState<string | null>(null)

  // Knowledge functions
  const loadKnowledge = useCallback(async () => {
    try {
      setKnowledgeLoading(true)
      const data = await storagesApi.list()
      setKnowledge(data)
      setKnowledgeLoaded(true)
    } catch (error) {
      console.error('Failed to fetch knowledge:', error)
    } finally {
      setKnowledgeLoading(false)
    }
  }, [])

  // Lazy-load knowledge data only when that tab is selected
  useEffect(() => {
    if (activeTab === 'knowledge' && !knowledgeLoaded && !knowledgeLoading) {
      loadKnowledge()
    }
  }, [activeTab, knowledgeLoaded, knowledgeLoading, loadKnowledge])

  const handleDeleteKnowledge = async (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      setDeletingKnowledgeId(id)
      await storagesApi.delete(id)
      setKnowledge(prev => prev.filter(k => k.id !== id))
    } catch (error) {
      console.error('Failed to delete knowledge base:', error)
      notify.error('Failed to delete knowledge base')
    } finally {
      setDeletingKnowledgeId(null)
    }
  }

  const formatDate = (dateString: string) => {
    const locale = intlLocaleForUi(i18n.language)
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return {
    knowledge,
    knowledgeLoading,
    deletingKnowledgeId,
    handleDeleteKnowledge,
    formatDate,
    formatRelativeTime,
  }
}
